import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteWithSearch,
  CheckboxInput,
  CurrencyDisplay,
  CustomTable,
  DatePicker,
  FormLabel,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SectionLabel,
  TextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, wordCapitalize } from "utils";

const AddPaymentComposerInvoice = () => {
  const classes = globalStyles();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [optionComposer, setOptionComposer] = useState([]);
  const [payload, setPayload] = useState({
    composer_id: null,
    date: moment(),
    notes: "",
    composer_invoice_ids: [],
    payment_number: "",
  });
  const [listInvoice, setListInvoice] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const totalInvoiceValue = selectedInvoices.reduce(
    (total, item) => total + item.invoice_value,
    0
  );
  const { composer_id, date, notes, payment_number } = payload;
  const [hasMore, setHasMore] = useState(true);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleCheckInvoice = (event, option) => {
    const { checked } = event?.target || false;
    setSelectedInvoices(prev =>
      checked
        ? [...prev, option]
        : prev.filter(item => item.invoice_id !== option.invoice_id)
    );
  };
  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getOptionComposer = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/list-composer`,
        {
          headers,
          params: filterParams,
        }
      );
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.composer_id,
      }));
      setOptionComposer(prev => {
        const combined =
          filterParams?.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const debounceOptionComposer = useCallback(
    debounce(() => {
      getOptionComposer();
    }, 500),
    [filterParams]
  );
  const getInvoiceList = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/payment/list-composer-invoice`,
        {
          headers,
          params: { ...queryParams, composer_id },
        }
      );
      const { data, meta } = res.data;
      setListInvoice(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const composers = [];
    selectedInvoices.map(item => {
      composers.push(item.invoice_id);
    });
    const modifiedPayload = {
      payment_number,
      date: moment(date).format("DD-MM-YYYY"),
      composer_id,
      notes,
      composer_invoice_ids: composers,
    };
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${hardBaseUrl}/publisher/transaction/v2/payment`,
        modifiedPayload,
        { headers }
      );
      ModalSuccess(res?.data?.message).then(() =>
        history.push("/admin/composer-invoice")
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getInvoiceList();
  }, [composer_id, queryParams]);
  useEffect(() => {
    const isSearching = Boolean(filterParams?.search);
    if (isSearching) {
      debounceOptionComposer();
      return () => {
        debounceOptionComposer.cancel();
      };
    } else {
      getOptionComposer();
    }
  }, [filterParams, debounceOptionComposer]);

  return (
    <Page className={classes.root} title="Payment">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Create Payment"
          breadcrumbData={breadcrumbData}
          backButton
        />
        <Divider className={classes.divider} />
        <SectionLabel
          title="Create Transaction"
          subTitle="Feature for making payment transactions to composer/author"
          mb="16px"
        />
        <Box border="1px solid #ebebeb" borderRadius="5px" p="24px" my="16px">
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item xs={12} md={6}>
              <FormLabel label="Payment No" />
              <TextInput
                placeholder="Payment No"
                value={payment_number}
                onChange={e =>
                  handleChangePayload(e?.target?.value, "payment_number")
                }
                rows={4}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel label="Date" />
              <DatePicker
                value={date}
                onChange={date => handleChangePayload(date, "date")}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormLabel label="Composer" />
              <AutoCompleteWithSearch
                loading={loadingFilter}
                options={optionComposer}
                value={
                  optionComposer?.find(option => option.id === composer_id) ||
                  null
                }
                onChange={value =>
                  handleChangePayload(value?.id, "composer_id")
                }
                inputValue={filterParams?.search}
                onInputChange={value =>
                  handleChangeFilterParams(value, "search")
                }
                optionLabel="composer_name"
                placeholder="Search Composer/Author"
                setQueryParams={setFilterParams}
                hasMore={hasMore}
                disabled={submitting}
                width="100%"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel label="Note" />
              <TextInput
                placeholder="Note"
                value={notes}
                onChange={e => handleChangePayload(e?.target?.value, "notes")}
                rows={4}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <SectionLabel title="List Invoices" mb="16px" />
              <CustomTable
                isLoading={loadingPage}
                columnTable={columnTableInvoice}
                data={listInvoice}
                page={queryParams?.page}
                pageSize={queryParams?.size}
                pageCount={tableTotalPage}
                handleChangePage={(_, page) =>
                  handleChangeQueryParams(page, "page")
                }
                handleChangePageSize={e =>
                  handleChangeQueryParams(e?.target?.value, "size")
                }
                checkedAction={item =>
                  selectedInvoices?.some(
                    selected => selected?.invoice_id === item?.invoice_id
                  )
                }
                handleCheckboxAction={(e, item) => handleCheckInvoice(e, item)}
                isHaveAction
                renderAction={item => {
                  return (
                    <CheckboxInput
                      checked={selectedInvoices?.some(
                        selected => selected?.invoice_id === item?.invoice_id
                      )}
                      onChange={e => handleCheckInvoice(e, item)}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
          <Box bgcolor="#fafafa" borderRadius="6px" p="14px" mt="24px">
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              mt="10px"
            >
              <Grid item fontSize="18px" fontWeight="bold">
                Total Value
              </Grid>
              <Grid item>
                <Typography fontSize="18px" fontWeight="bold">
                  <CurrencyDisplay
                    value={totalInvoiceValue}
                    prefix={"Rp"}
                    decimalScale={2}
                    suffix=",-"
                  />
                </Typography>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
          </Box>
        </Box>
        <Grid container justifyContent="right" my="16px">
          <PrimaryButton
            label={submitting ? "Submitting" : "Send to Approval"}
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          />
        </Grid>
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Transaction",
    link: "/admin/payment",
  },
  {
    label: "Create Payment",
    active: true,
  },
];

const columnTableInvoice = [
  {
    name: "invoice_date",
    title: "Invoice Date",
    renderText: item => moment(item).format("DD MMMM YYYY"),
  },
  {
    name: "invoice_number",
    title: "Invoice Number",
  },
  {
    name: "composer_name",
    title: "Composer",
  },
  {
    name: "invoice_value",
    title: "Value Invoice",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
  },
  {
    name: "generated_by",
    title: "Generated By",
  },
  {
    name: "status",
    title: "Status",
    renderText: item => wordCapitalize(item),
  },
];

export default AddPaymentComposerInvoice;
